import { useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useModalConfirm } from '@/core/context/ModalContext';
import { logout } from '@/modules/auth/redux/authSlice';
import type { AppDispatch } from '@/store/store';

/** Asks for confirmation (ongoing uploads get interrupted) before clearing the session. */
export const useLogoutConfirm = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showConfirm } = useModalConfirm();
  const { t } = useTranslation(['common']);
  const confirmOpenRef = useRef(false);

  return useCallback(() => {
    if (confirmOpenRef.current) {
      return;
    }

    confirmOpenRef.current = true;

    showConfirm({
      centered: true,
      width: 420,
      className: 'logout-confirm',
      icon: null,
      title: t('common:common.logout_confirm_title'),
      content: t('common:common.logout_confirm_message'),
      okText: t('common:common.logout_confirm_action'),
      cancelText: t('common:common.logout_confirm_cancel'),
      okButtonProps: {
        type: 'default',
        danger: true,
      },
      cancelButtonProps: {
        type: 'default',
      },
      onCancel: () => {
        confirmOpenRef.current = false;
      },
      onOk: async () => {
        try {
          queryClient.clear();
          await dispatch(logout());
          navigate('/login', { replace: true });
        } finally {
          confirmOpenRef.current = false;
        }
      },
    });
  }, [dispatch, navigate, queryClient, showConfirm, t]);
};
